/* Copyright (c) 2021 Caring Transitions Online Auctions. All Rights Reserved. */

// library imports
import { Component, createRef } from 'react';
import { Form, Button, Spinner, Overlay, Tooltip, ProgressBar } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Axios from 'axios';

// project imports
import { getBuyersUrl, getBuyerHeaders } from 'shared/http';
import Validators from 'shared/validators';
import STRENGTH from 'features/constants/PasswordStrength';

// style imports
import './SetPassword.scss';
import store from 'features/store';
import ToasterMessage from 'shared/toast/toast';
import { MyAccountService } from 'shared/services';

/**
 * this page is used to reset new password on valid token
 * 
 * @param token
 * 
 * @returns html code to render set new password on valid token else show invalid message
 * 
**/

export class SetPassword extends Component {
    passwordForm;

    constructor(props) {
        super(props);

        this.toasterMessage = createRef();
        this.state = {
            // form
            formControls: {
                newPassword: { value: '', valid: false, error: '', validators: [this.compareRetypeField, this.checkPasswordStrength, Validators.required()] },
                retypePassword: { value: '', valid: false, error: '', validators: [Validators.required(), this.compareNewField] }
            },
            formValid: false,

            // shows the strength of entered password
            passwordStrength: { status: 0, lower: false, upper: false, numeric: false, count: false, symbol: false },

            // token from url
            token: '',

            // toggle input type
            showNewPassword: false,
            showRetypePassword: false,

            // show or hide strength popup
            showStrengthOverlay: false,

            // loading
            passwordFormLoading: false,

            // show page based on token valididty
            tokenValidity: 0,

            // Window innerwidth
            windowWidth: window.innerWidth
        };
        this.passwordForm = createRef();
    }

    componentDidMount() {
        this.validateToken();
        this.removeLoader();
        window.sessionStorage.setItem('registerModalOpened', true);
        window.addEventListener('resize', this.updateDimensions);
    }

    componentDidUpdate() {
        window.addEventListener('resize', this.updateDimensions);
    }

    /* Detect window width */
    updateDimensions = () => {
        this.setState({
            ...this.state,
            windowWidth: window.innerWidth,
            windowOrientation: window.orientation
        });
    };

    removeLoader = () => {
        const loader = document.querySelector('.pre-loader');
        const root = document.querySelector('.ct-bids');
        // console.log(loader, root);
        loader.classList.add('hide-loader');
        root.classList.add('loaded');
    }

    /* checks whether the token is valid */
    validateToken = async () => {
        const token = this.props.location.search;
        if (token.includes('?token=')) {
            this.setState({
                token: token.replace('?token=', '')
            });

            await Axios.get(getBuyersUrl(`/api/v1/buyer/user/activate/password/set/${token.replace('?token=', '')}`), getBuyerHeaders(false))
                .then(response => {
                    this.setState({ tokenValidity: 1 });
                })
                .catch(error => {
                    this.setState({ tokenValidity: -1 });
                    // TODO: Error message handle
                });
        } else {
            this.props.history.push('/not-found');
        }
    };

    /* validation of password strength */
    checkPasswordStrength = (password) => {
        const lowerRegExp = new RegExp("^(?=.*[a-z])");
        const upperRegExp = new RegExp("^(?=.*[A-Z])");
        const numericRegExp = new RegExp("^(?=.*[0-9])");
        const lengthRegExp = new RegExp("^(?=.{8,15})");
        const symbolRegExp = new RegExp(/[ !@#$%^&*]/);

        let strength = {
            status: 0,
            lower: false,
            upper: false,
            numeric: false,
            symbol: false,
            count: false
        };

        /* check for lowercase character in password */
        if (lowerRegExp.test(password)) {
            strength.status += 1;
            strength.lower = true;
        }

        /* check for uppercase character in password */
        if (upperRegExp.test(password)) {
            strength.status += 1;
            strength.upper = true;
        }

        /* check for number in password */
        if (numericRegExp.test(password)) {
            strength.status += 1;
            strength.numeric = true;
        }

        /* check for Minimum 8 - Maximum 15 characters */
        if (lengthRegExp.test(password) && password.length < 16) {
            strength.status += 1;
            strength.count = true;
        }

        /* check for symbol in password */
        if (symbolRegExp.test(password)) {
            strength.status += 1;
            strength.symbol = true;
        }

        /* set password strength */
        this.setState({ passwordStrength: { ...strength } });

        if (!password || strength.status === 5) {
            return '';
        }

        return 'Password should follow rules';
    };

    /* hide tooltip on input blur when no vlaue */
    hideOverlay = () => {
        if (!this.state.formControls.newPassword.value || this.state.formControls.newPassword.valid) {
            this.setState({ showStrengthOverlay: false });
        }
    };

    /* show tooltip on input focus */
    showOverlay = () => {
        this.setState({ showStrengthOverlay: true });
    };

    /* toggles password to show or hide on clicking eye icon */
    toggleNewPassword = () => {
        this.setState({ showNewPassword: !this.state.showNewPassword });
    };

    /* toggles password to show or hide on clicking eye icon */
    toggleRetypePassword = () => {
        this.setState({ showRetypePassword: !this.state.showRetypePassword });
    };

    compareNewField = (value) => {
        if (value === this.state.formControls.newPassword.value) return '';

        return 'Password does not match';
    };

    compareRetypeField = (value) => {
        if (this.state.formControls.retypePassword.value) {
            setTimeout(() => {
                if (this.state.formControls.retypePassword.value !== value) {
                    this.setState({
                        formControls: {
                            ...this.state.formControls,
                            retypePassword: {
                                ...this.state.formControls.retypePassword,
                                error: 'Password does not match',
                                valid: false
                            }
                        },
                        formValid: false
                    });
                } else if (this.state.formControls.retypePassword.value === value) {
                    this.setState({
                        formControls: {
                            ...this.state.formControls,
                            retypePassword: {
                                ...this.state.formControls.retypePassword,
                                error: '',
                                valid: true
                            }
                        }
                    });
                    this.setState({ formValid: Validators.checkFormValid({ ...this.state.formControls }) });
                }
            });
        }

        return '';
    };

    onInputValidation = (event) => {
        let controlError = '';

        event.target.value = Validators.checkPassword(event.target.value, this.state.formControls[event.target.name].value);

        /* checks all the validators for the control */
        for (let validator of this.state.formControls[event.target.name].validators) {
            controlError = validator(event.target.value);
            if (controlError) break;
        }

        this.setState({
            formControls: {
                ...this.state.formControls,
                [event.target.name]: {
                    value: event.target.value,
                    valid: controlError ? false : true,
                    error: controlError,
                    validators: [...this.state.formControls[event.target.name].validators]
                }
            }
        }, () => {
            /* check form validity */
            if (controlError) {
                this.setState({ formValid: false });
            } else {
                let formValid = true;
                for (let control of Object.keys(this.state.formControls)) {
                    if (!this.state.formControls[control].valid) {
                        formValid = false;
                        break;
                    }
                }
                this.setState({ formValid: formValid });
            }
        });
    };

    onSavePassword = () => {
        if (this.state.formValid) {
            const data = {
                context: this.state.token,
                password: btoa(this.state.formControls.newPassword.value)
            };

            this.setState({ passwordFormLoading: true });
            MyAccountService.setPassword({ data: data })
                .then(response => {
                    this.setState({ passwordFormLoading: false });
                    // TODO: success message handle
                    this.toasterMessage.current.showMessage({ type: 'success', message: "Password changed successfully" });
                    this.props.history.push('/');
                    store.dispatch({ type: 'TOGGLE_SIGNIN_MODAL', payload: { open: true, mode: 'Sign-In' } });
                })
                .catch(error => {
                    this.toasterMessage.current.showMessage({ type: 'danger', message: error.message });
                    this.setState({ passwordFormLoading: false });
                    // TODO: Error message handle
                });
        }
    };

    signInOpen = () => {
        this.props.history.push('/');
        store.dispatch({ type: 'TOGGLE_SIGNIN_MODAL', payload: { open: true, mode: 'Sign-In' } });
    }

    isLandscape() {
        return (window.orientation === 90 || window.orientation === -90);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateDimensions);
    }

    render() {

        return (
            <div className="d-flex flex-column justify-content-center align-items-center bg-primary set-password">
                {
                    this.state.tokenValidity === 0 ?
                        <Spinner animation="border" variant="info" className="spinner-3x" /> :
                        <div className="d-flex flex-column bg-white p-4 set-password-card">
                            <div className="d-flex flex-row justify-content-center mb-4">
                                <img src={`${process.env.PUBLIC_URL}/static/logo/CTBids_logo.png`} alt="logo" height="auto" width="170px" />
                            </div>
                            {
                                this.state.showStrengthOverlay && this.state.windowWidth < 993 && (this.state.windowOrientation !== 90 || this.state.windowOrientation !== -90) ?
                                    <div className="mobile-view-password-strength">
                                        <b className="mb-3">
                                            Password Strength:&nbsp;
                                            <span className={`text-${STRENGTH[this.state.passwordStrength.status]['color']}`}>
                                                {STRENGTH[this.state.passwordStrength.status]['name']}
                                            </span>
                                        </b>
                                        <ProgressBar variant={STRENGTH[this.state.passwordStrength.status]['color']} now={STRENGTH[this.state.passwordStrength.status]['progress']} />
                                        <ul className="list-unstyled">
                                            <li>
                                                <span>
                                                    <i className={this.state.passwordStrength.lower ? "fa fa-check text-success" : "fa fa-times"} />
                                                    &nbsp;Atleast 1 Lowercase (a-z)
                                                </span>
                                            </li>
                                            <li>
                                                <span>
                                                    <i className={this.state.passwordStrength.upper ? "fa fa-check text-success" : "fa fa-times"} />
                                                    &nbsp;Atleast 1 Uppercase (A-Z)
                                                </span>
                                            </li>
                                            <li>
                                                <span>
                                                    <i className={this.state.passwordStrength.numeric ? "fa fa-check text-success" : "fa fa-times"} />
                                                    &nbsp;Atleast 1 Number (0-9)
                                                </span>
                                            </li>
                                            <li>
                                                <span>
                                                    <i className={this.state.passwordStrength.count ? "fa fa-check text-success" : "fa fa-times"} />
                                                    &nbsp;Between 8 to 15 Characters
                                                </span>
                                            </li>
                                            <li>
                                                <span>
                                                    <i className={this.state.passwordStrength.symbol ? "fa fa-check text-success" : "fa fa-times"} />
                                                    &nbsp;Atleast 1 Symbol Character
                                                </span>
                                            </li>
                                            <li className="">Allowed Symbol Characters (!, @, #, $, %, ^, &, *)</li>
                                        </ul>
                                    </div> : null
                            }
                            <div className={`d-flex flex-row ${(!this.state.showStrengthOverlay && this.state.windowWidth < 769) ? "mt-5" : ""} ${this.state.windowWidth > 768 ? "mt-5" : ""}`}>
                                <h5 className="text-secondary"><b>Set Password</b></h5>
                            </div>

                            {
                                this.state.tokenValidity === 1 ?
                                    <div className="d-flex flex-column">
                                        <div className="d-flex flex-column">
                                            <div className="float-control" ref={this.passwordForm}>
                                                <div className="text">
                                                    {
                                                        this.state.formControls.newPassword.valid ? null : <small className="error">{this.state.formControls.newPassword.error}</small>
                                                    }
                                                </div>
                                                {
                                                    this.state.showNewPassword ?
                                                        <i className="password-icon fa fa-eye fa-flip-horizontal" onClick={this.toggleNewPassword} /> :
                                                        <i className="password-icon fa fa-eye-slash fa-flip-horizontal" onClick={this.toggleNewPassword} />
                                                }
                                                <Form.Control autoFocus type={this.state.showNewPassword ? 'text' : 'password'} id="new-password"
                                                    className={this.state.formControls.newPassword.error ? 'error-control' : ''} autoComplete="off"
                                                    value={this.state.formControls.newPassword.value} name="newPassword" placeholder=" "
                                                    onBlur={this.hideOverlay} onFocus={this.showOverlay} onChange={this.onInputValidation} />
                                                <label htmlFor="new-password">New Password</label>
                                            </div>
                                            <Overlay transition={false} container={this.passwordForm} id="overlay"
                                                placement={window.innerWidth > 760 ? "right" : "bottom"}
                                                show={this.state.showStrengthOverlay && this.state.windowWidth > 992}>
                                                <Tooltip className="overlay-tooltip">
                                                    <div>
                                                        <b className="mb-3">
                                                            Password Strength:&nbsp;
                                                            <span className={`text-${STRENGTH[this.state.passwordStrength.status]['color']}`}>
                                                                {STRENGTH[this.state.passwordStrength.status]['name']}
                                                            </span>
                                                        </b>
                                                        <ProgressBar variant={STRENGTH[this.state.passwordStrength.status]['color']} now={STRENGTH[this.state.passwordStrength.status]['progress']} />
                                                        <ul className="list-unstyled">
                                                            <li>
                                                                <span>
                                                                    <i className={this.state.passwordStrength.lower ? "fa fa-check text-success" : "fa fa-times"} />
                                                                    &nbsp;Atleast 1 Lowercase (a-z)
                                                                </span>
                                                            </li>
                                                            <li>
                                                                <span>
                                                                    <i className={this.state.passwordStrength.upper ? "fa fa-check text-success" : "fa fa-times"} />
                                                                    &nbsp;Atleast 1 Uppercase (A-Z)
                                                                </span>
                                                            </li>
                                                            <li>
                                                                <span>
                                                                    <i className={this.state.passwordStrength.numeric ? "fa fa-check text-success" : "fa fa-times"} />
                                                                    &nbsp;Atleast 1 Number (0-9)
                                                                </span>
                                                            </li>
                                                            <li>
                                                                <span>
                                                                    <i className={this.state.passwordStrength.count ? "fa fa-check text-success" : "fa fa-times"} />
                                                                    &nbsp;Between 8 to 15 Characters
                                                                </span>
                                                            </li>
                                                            <li>
                                                                <span>
                                                                    <i className={this.state.passwordStrength.symbol ? "fa fa-check text-success" : "fa fa-times"} />
                                                                    &nbsp;Atleast 1 Symbol Character
                                                                </span>
                                                            </li>
                                                            <li className="">Allowed Symbol Characters (!, @, #, $, %, ^, &, *)</li>
                                                        </ul>
                                                    </div>
                                                </Tooltip>
                                            </Overlay>
                                            <div className="float-control">
                                                <div className="text">
                                                    {
                                                        this.state.formControls.retypePassword.valid ? null : <small className="error">{this.state.formControls.retypePassword.error}</small>
                                                    }
                                                </div>
                                                {
                                                    this.state.showRetypePassword ?
                                                        <i className="password-icon fa fa-eye fa-flip-horizontal" onClick={this.toggleRetypePassword} /> :
                                                        <i className="password-icon fa fa-eye-slash fa-flip-horizontal" onClick={this.toggleRetypePassword} />
                                                }
                                                <Form.Control type={this.state.showRetypePassword ? 'text' : 'password'} id="retype-password"
                                                    className={this.state.formControls.retypePassword.error ? 'error-control' : ''} autoComplete="off"
                                                    value={this.state.formControls.retypePassword.value} name="retypePassword" placeholder=" "
                                                    onChange={this.onInputValidation} />
                                                <label htmlFor="retype-password">Retype Password</label>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-center">
                                            <ToasterMessage className="alert-handle" ref={this.toasterMessage}></ToasterMessage>
                                        </div>
                                        <div className="d-flex flex-row justify-content-between my-3">
                                            <div className="d-flex flex-row">
                                                <Button variant="secondary" style={{ width: '100px' }} onClick={this.onSavePassword}
                                                    disabled={!this.state.formValid || this.state.passwordFormLoading}>
                                                    Save  {
                                                        this.state.passwordFormLoading ?
                                                            <Spinner animation="border" variant="info" style={{ margin: '0 8px' }} /> :
                                                            null
                                                    }
                                                </Button>

                                            </div>
                                            <div className="d-flex align-items-center clickable" onClick={this.signInOpen}>
                                                <small className="text-secondary">Back To Sign In</small>
                                            </div>
                                        </div>
                                    </div> :
                                    <div className="d-flex flex-column my-5">
                                        <p>The link you provided to set password is either invalid or expired.</p>
                                        <div className="d-flex flex-row justify-content-end">
                                            <Link to="/">
                                                <small className="text-secondary">Back To Sign In</small>
                                            </Link>
                                        </div>
                                    </div>
                            }
                        </div>
                }
            </div>
        );
    }
}
